import React, { useEffect, useMemo, MouseEventHandler, useState } from "react";

import { useTranslation } from "react-i18next";

import { Formik, FormikHelpers } from "formik";
import * as yup from "yup";

import {
  HvButton,
  HvDialog,
  HvDialogTitle,
  HvDialogContent,
  HvDialogActions,
  HvInput,
  HvCheckBox,
  HvBanner,
  HvDropdown,
  ListValueProp,
} from "@hitachivantara/uikit-react-core";

import { AttributeFormData } from "lib/api/incidentType";

import useStyles from "./styles";

export type AddEditPropertiesDialogProps = {
  isOpen: boolean;
  onSave: (
    data: AttributeFormData,
    addAnother?: boolean
  ) => AttributeFormData | undefined;
  onClose: (success: boolean, message?: string) => void;
  initialValues?: AttributeFormData;
  mode: "add" | "edit";
};

const AddEditPropertiesDialog: React.FC<AddEditPropertiesDialogProps> = ({
  isOpen,
  onSave,
  onClose,
  initialValues,
  mode,
}) => {
  const { t } = useTranslation("addEditIncidentType");
  const classes = useStyles();
  const [addAnother, setAddAnother] = useState(false);
  const [bannerOpened, setBannerOpened] = useState(false);
  const [bannerStepIndex, setBannerStepIndex] = useState(0);

  const validationSchema = useMemo(() => {
    return yup.object({
      label: yup.string().required(t("validation.displayLabelRequired")),
      type: yup.string().required(t("validation.typeRequired")),
      visibility: yup.string().required(t("validation.visibilityRequired")),
    });
  }, [t]);

  useEffect(() => {
    if (bannerOpened) {
      const timeout = setTimeout(() => setBannerOpened(false), 5000);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [bannerOpened]);

  const onSubmitHandler = async (
    values: AttributeFormData,
    actions: FormikHelpers<AttributeFormData>
  ) => {
    try {
      const attribute = onSave(values, addAnother);

      if (attribute) {
        setBannerStepIndex((attribute.order as number) || 0);
        if (addAnother) {
          setBannerOpened(true);
        }
      }
      actions.resetForm({
        values: {
          label: "",
          type: "",
          visibility: "",
        },
      });
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (error.message === "name_already_exists") {
        actions.setFieldError(
          "label",
          t("validation.displayLabelAlreadyTaken")
        );
      }
    }
  };

  const handleClose = () => {
    setAddAnother(false);
    onClose?.(false);
    setBannerOpened(false);
  };

  const handleDialogClose = (
    event: MouseEventHandler<HTMLButtonElement>,
    reason: unknown
  ) => {
    if (reason !== "backdropClick") {
      handleClose();
    }
  };

  const renderBanner = useMemo(
    () => (
      <HvBanner
        className={classes.banner}
        style={{ top: undefined }}
        open={bannerOpened}
        variant="success"
        showIcon
        label={t(`propertiesDialog.success`, {
          order: bannerStepIndex + 1,
        })}
        onClose={() => setBannerOpened(false)}
      />
    ),
    [bannerOpened, bannerStepIndex, classes.banner, t]
  );

  return (
    <HvDialog
      buttonTitle={t("buttons.close")}
      classes={{ paper: classes.paper }}
      open={isOpen || addAnother}
      onClose={handleDialogClose}
    >
      <Formik
        initialValues={
          initialValues || {
            label: "",
            type: "",
            visibility: "",
          }
        }
        validationSchema={validationSchema}
        onSubmit={onSubmitHandler}
      >
        {(props) => {
          const {
            values,
            errors,
            touched,
            handleSubmit,
            setFieldValue,
            setFieldTouched,
            isSubmitting,
            isValid,
          } = props;

          const parseStatus = (field: keyof AttributeFormData) =>
            errors[field] && touched[field] ? "invalid" : "valid";

          const parseStatusMessage = (field: keyof AttributeFormData) => {
            return errors[field] && touched[field] ? errors[field] : "";
          };

          const handleFieldValue = (fieldName: string, fieldValue?: string) => {
            if (bannerOpened) setBannerOpened(false);
            setFieldValue(fieldName, fieldValue);
          };

          const typeDropdownValues = [
            "boolean",
            "date and time",
            "hyperlink",
            "image",
            "numeric",
            "text",
            "textarea",
            "video",
          ].map((id) => ({
            id,
            label: t(
              `propertiesDialog.fields.type.options.${id.replaceAll(" ", "_")}`
            ),
            selected: id === values?.type,
          }));

          const visibilityDropdownValues = [
            "always",
            "hidden",
            "only_detail",
          ].map((id) => ({
            id,
            label: t(`propertiesDialog.fields.visibility.options.${id}`),
            selected: id === values?.visibility,
          }));

          return (
            <form onSubmit={handleSubmit} noValidate>
              <HvDialogTitle className={classes.title}>
                {t(`propertiesDialog.title.${mode}`)}
              </HvDialogTitle>
              <HvDialogContent>
                {renderBanner}
                <HvInput
                  className={classes.input}
                  inputProps={{
                    autoComplete: "off",
                  }}
                  name="label"
                  required
                  label={t("propertiesDialog.fields.label.label")}
                  placeholder={t("propertiesDialog.fields.label.placeholder")}
                  value={values.label}
                  status={parseStatus("label")}
                  statusMessage={parseStatusMessage("label")}
                  onBlur={() => setFieldTouched("label")}
                  onChange={(evt, value) => handleFieldValue("label", value)}
                />
                <HvDropdown
                  name="type"
                  className={classes.input}
                  values={typeDropdownValues}
                  required
                  disabled={mode === "edit"}
                  label={t("propertiesDialog.fields.type.label")}
                  placeholder={t("propertiesDialog.fields.type.placeholder")}
                  status={parseStatus("type")}
                  statusMessage={parseStatusMessage("type")}
                  onToggle={() => setFieldTouched("type", true, false)}
                  onChange={(
                    selection: ListValueProp | ListValueProp[] | undefined
                  ) => {
                    const selectedValue = selection as ListValueProp;
                    handleFieldValue(
                      "type",
                      selectedValue ? selectedValue.id : undefined
                    );
                  }}
                />
                <HvDropdown
                  name="visibility"
                  className={classes.input}
                  values={visibilityDropdownValues}
                  required
                  label={t("propertiesDialog.fields.visibility.label")}
                  placeholder={t(
                    "propertiesDialog.fields.visibility.placeholder"
                  )}
                  status={parseStatus("visibility")}
                  statusMessage={parseStatusMessage("visibility")}
                  onToggle={() => setFieldTouched("visibility", true, false)}
                  onChange={(
                    selection: ListValueProp | ListValueProp[] | undefined
                  ) => {
                    const selectedValue = selection as ListValueProp;
                    handleFieldValue(
                      "visibility",
                      selectedValue ? selectedValue.id : undefined
                    );
                  }}
                />
              </HvDialogContent>
              <HvDialogActions>
                {mode === "add" && (
                  <HvCheckBox
                    checked={addAnother}
                    onChange={(_evt, checked) => {
                      setAddAnother(checked);
                      setBannerOpened(false);
                    }}
                    label={t("propertiesDialog.buttons.addAnother")}
                  />
                )}
                <div style={{ flex: 1 }} />
                <HvButton
                  category="ghost"
                  type="submit"
                  disabled={isSubmitting || !isValid || values.label === ""}
                >
                  {t(`propertiesDialog.buttons.${mode}`)}
                </HvButton>
                <HvButton
                  category="ghost"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  {t("propertiesDialog.buttons.cancel")}
                </HvButton>
              </HvDialogActions>
            </form>
          );
        }}
      </Formik>
    </HvDialog>
  );
};

export default AddEditPropertiesDialog;
